import { gql, useQuery } from '@apollo/client';
import type { Any } from '@livecontrol/core-utils';
import _ from 'lodash';
import { useMemo } from 'react';
import type {
  GetScheduledEventQuery,
  GetScheduledEventQueryVariables,
  GetWebPlayerAndAssetsQuery,
  GetWebPlayerAndAssetsQueryVariables
// eslint-disable-next-line import/no-internal-modules
} from './__generated__/use-scheduled-event';
import { useEventSubscription } from './use-subscription';

const PreferencesFragment = gql`
fragment PreferencesFragment on EventPreferences {  
  audienceShots {
    category
    content
    option
  }
  wideVenueShots {
    option
    content
    category
  }
  quickShots {
    category
    option
    content
  }
  eventEnding {
    category
    option
    content
  }
  detailedViewShots {
    category
    option
    content
  }
  closeUpShots {
    category
    option
    content
  }
}
`;

const LocationFragment = gql`
fragment LocationFragment on Location {
  id
  secondary_id
  name
  zoom
  splashtop_id
  splashtop_uuid
  splashtop_name
  mux_stream_url
  mux_stream_key
  mux_stream_id
  screenshare
  is_simulated_live
  is_disabled
  is_static
  is_produced
  is_client_booking
  is_mobile
  cue_sheet_enabled
  production_notes {
    notes {
      content
      category
      content
      option
    }
    id
  }
  user {
    id
    time_zone
    organization_name
    event_operator_notes
    support_notes
    first_name
    last_name
    email
    phone_number
    contacts {
      id
      first_name
      phone_number
      notes
      email
    }
    userNotes {
      id
      hardware_notes
      audio_notes
      account_notes
      image_notes
      network_notes
      technical_notes
    }
  }
}
`;

const DestinationFragment = gql`
fragment DestinationFragment on EventDestination {
  streamUrl
    name
    type
    status
    destinationPlayerUrl
    active
    streamBroadcastId
    streamPasstrough
    streamKey
    streamSecondaryBroadcastId
}
`;

const standardEventFragment = gql`
fragment StandardEventFragment on EventDetails {
  asset_url
  assignedOperator
  background_url
  banner
  castr_facebook
  castr_youtube
  checked_user {
    id
    first_name
    last_name
  }
  client_booking_payment_token
  client_booking_bill_later
  client_booking_customer_email
  client_booking_customer_name
  client_booking_customer_phone
  client_booking_upgrade_identifier
  cue_sheet_url
  audio_cue_sheet_url
  date_created
  date_updated
  description
  enable_castr
  end_time
  event_password
  hidden
  id
  is_simulated_live
  is_operated
  name
  notification_sent
  live_chat
  linkedEvent {
    id
    name
    technical_contact {
      id
      onsite_technical_priority
      user {
        id
        first_name
        last_name
        email
        phone_number
        preferred_contact_method
      }
    }
  }
  operatorInfo {
    checklist_audio
    checklist_correct_encoder
    checklist_notes
    checklist_obs
    checklist_presets
    checked_in
  }
  operator_notes
  operatorType
  preferences{
    ...PreferencesFragment
  }
  redirect_url
  record_only
  simulated_live_filename
  simulated_live_asset_id
  start_time
  status
  stream_live
  stream_facebook
  stream_rtmp
  stream_youtube
  stream_zoom
  type
  event_type
  was_ever_public
  webplayer_url
  webplayer_path
  zoom_meeting_id
  zoom_password
  integrations {
    integration
  }
  operator {
    id
    date_updated
    date_created
    confirmation
  }
  operator_name
  operator_id
  high_priority
  high_priority_note
  is_first_event
  verifier_email
  receive_iso_recordings
  audioContact: sync_contact {
    id
    first_name
    last_name
    email
    phone_number
    preferred_contact_method
    phone_type
    notes
    priority
  }
  technical_contact {
    id
    onsite_technical_priority
    user {
      id
      first_name
      last_name
      email
      phone_number
      preferred_contact_method
    }
  }
  on_site_contact {
    first_name
    last_name
    email
    phone_number
    preferred_contact_method
  }
  destinations {
    ...DestinationFragment
  }
  location {
    ...LocationFragment
  }
}
${DestinationFragment}
${LocationFragment}
${PreferencesFragment}
`;

export const QUERY = gql`
query GetEvent(
  $eventId: String!
) {
  getEventDetails(event: { id: $eventId }) {
    ...StandardEventFragment
    webplayer_placeholder_image
  }
}
${standardEventFragment}
`;

export const GET_WEBPLAYER_DATA = gql`
query GetWebPlayerData(
  $clientIdInt: Int!
  $eventId: Float
) {
  listAssets(clientId: $clientIdInt, scheduledEventId: $eventId) {
    stream_url
    mux_asset_id
    is_live
    id
  }
}
`;

export type ScheduledEvent =  Exclude<
Exclude<GetScheduledEventQuery['getEventDetails'], undefined>,
undefined
>;

export const vars: Any = {
  value: {}
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useScheduledEvent = (eventId: string) => {
  const { data, loading, refetch: refetchEvent } = useQuery<
    GetScheduledEventQuery,
    GetScheduledEventQueryVariables
  >(QUERY, {
    variables: {
      eventId
    }
  });

  const { data: extraEventDetails, loading: loadingExtraDetails, refetch: refetchWebPlayer } = useQuery<
  GetWebPlayerAndAssetsQuery,
  GetWebPlayerAndAssetsQueryVariables
  >(GET_WEBPLAYER_DATA, {
    variables: {
      clientIdInt: Number(data?.getEventDetails?.location?.user?.id),
      eventId: Number(eventId)
    },
    skip: (!data?.getEventDetails || !data.getEventDetails.location?.user?.id)
  });

  // Subscribe for changes.
  useEventSubscription(eventId);

  const value = useMemo((): ScheduledEvent | undefined => {
    if (!eventId || !data?.getEventDetails) {
      return undefined;
    }

    return {
      ...data.getEventDetails,
      assets: extraEventDetails?.listAssets?.at(0)
    };
  }, [data, eventId, extraEventDetails]);

  const refetch = async (): Promise<unknown[]> => {
    const promises = [refetchEvent()];

    if (data?.getEventDetails?.location?.user?.id) {
      promises.push(refetchWebPlayer());
    }

    return Promise.all(promises);
  };

  return {
    scheduledEvent: value,
    loading: loading || loadingExtraDetails,
    refetch
  };
};
